import { useState } from "react";

import { Button, ThumbsDownSVG, ThumbsUpSVG } from "@newsela/angelou";
import { objectTypes, actionNames, withTrackEvent } from "@newsela/snitch";

import { VIEW_PAGE_LUNA_AI_DESIGN } from "@/ai/constants/experiments";
import ActivityChoiceIndicator from "@/components/ActivityChoiceIndicator";
import AITopicsNotice from "@/components/AIComponents/AIContentNotice/AIContentNotice";
import useHasFeature from "@/features/useHasFeature";
import { GUIDED_HIGHLIGHTING_SKILLS_META } from "@/view/GuidedHighlightingContainer/utils/GuidedHighlighting.constants";
import { IGuidedHighlightingQuestion } from "@/view/utils/useGuidedHighlightingStudent/types";
import useGuidedHighLightingTeacher from "@/view/utils/useGuidedHighLightingTeacher";
import useReportContent from "@/view/utils/useReportContent";

import GuidedHighlightingTeacherFeedbackModal from "../GuidedHighlightingTeacherFeedbackModal";
import {
  answerWrapper,
  buttonsWrapper,
  cardWrapper,
  footWrapper,
  headerCss,
  reportButtonCss,
  titleCss,
  textCss,
  divider,
  indicatorWrapper,
  questionCss,
} from "./GuidedHighlightingTeacherSkillQuestion.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface GuidedHighlightingTeacherSkillQuestionProps {
  question: IGuidedHighlightingQuestion;
  index: number;
}

const GuidedHighlightingTeacherSkillQuestion = ({
  question,
  index,
}: GuidedHighlightingTeacherSkillQuestionProps) => {
  const [disabled, setDisabled] = useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [teacherReportModal, setTeacherReportModal] = useState(false);

  const { handleQuestionFeedbackThumbsUp } = useGuidedHighLightingTeacher();
  const { problemDescription: requestError } = useReportContent();

  const [showLunaAiBranding] = useHasFeature(VIEW_PAGE_LUNA_AI_DESIGN);

  const readingSkillName =
    GUIDED_HIGHLIGHTING_SKILLS_META[question.readingSkill].name;

  const handleThumbsUp = async () => {
    try {
      await handleQuestionFeedbackThumbsUp(question.id);
      if (!requestError) setButtonsDisabled(true);
    } catch (error) {
      console.error("Error submitting thumbs up feedback:", error);
    }
  };

  const handleReport = async (reaction: string) => {
    if (reaction === "thumbs_up") {
      await handleThumbsUp();
    } else {
      setTeacherReportModal(true);
    }
  };

  return (
    <div css={cardWrapper(disabled)} data-testid="QuestionCardWrapper">
      <div>
        <p css={titleCss}>Question {index + 1}</p>
        <div css={questionCss(showLunaAiBranding)}>
          <h4 css={headerCss}>{readingSkillName}</h4>
          <AITopicsNotice
            contentId={question.id}
            type="ChecksForUnderstanding"
          />
        </div>
      </div>
      <p css={textCss}>{question.question}</p>
      <div css={answerWrapper}>
        <div css={indicatorWrapper}>
          <ActivityChoiceIndicator
            labelColor="success"
            status="correct"
            text="Correct answer"
          />
        </div>
        <p css={textCss}>{question.suggestedAnswer?.content}</p>
      </div>
      <div css={footWrapper}>
        <p css={textCss}>Rate this question</p>
        <div css={buttonsWrapper}>
          <ButtonWithTrackEvent
            __cssFor={{ root: reportButtonCss }}
            flavor={Button.flavors.tertiary}
            icon={{
              SvgComponent: ThumbsUpSVG,
              alignment: Button.iconAlignments.left,
            }}
            disabled={disabled || buttonsDisabled}
            onClick={async () => await handleReport("thumbs_up")}
            objectType={objectTypes.report}
            actionName={actionNames.interact}
            dataProps={{
              "data-qa-selector": "open-gh-report-modal-with-like-button",
            }}
          >
            {buttonsDisabled ? "Liked" : "Like"}
          </ButtonWithTrackEvent>

          <ButtonWithTrackEvent
            __cssFor={{ root: reportButtonCss }}
            flavor={Button.flavors.tertiary}
            icon={{
              SvgComponent: ThumbsDownSVG,
              alignment: Button.iconAlignments.left,
            }}
            disabled={disabled || buttonsDisabled}
            onClick={async () => await handleReport("thumbs_down")}
            objectType={objectTypes.report}
            actionName={actionNames.interact}
            dataProps={{
              "data-qa-selector": "open-gh-report-modal-with-dislike-button",
            }}
          >
            Dislike
          </ButtonWithTrackEvent>
        </div>
      </div>

      <div css={divider} />
      <GuidedHighlightingTeacherFeedbackModal
        isOpen={teacherReportModal}
        closeModal={() => {
          setTeacherReportModal(false);
        }}
        setDisabled={() => setDisabled(true)}
        contentId={question.id}
      />
    </div>
  );
};

export default GuidedHighlightingTeacherSkillQuestion;
